/**
 * InviteCoachingModal — Coaching overlay shown alongside the invite send.
 *
 * The invite has already fired by the time this opens (see TherapistPanel's
 * handleInviteFromPrimaryBtn / handleInviteFromReInviteBtn). The modal's only
 * job is to surface read-aloud scripts the therapist follows while the patient
 * acts on the notification. It auto-closes the moment the patient connects.
 *
 * Does NOT extend shared/Modal.tsx (D-11): that component lacks a focus trap.
 */

import { useCallback, useEffect, useRef, useState } from 'react';

import stepOneImg from '../../assets/invite/step-1-open.png';
import stepTwoImg from '../../assets/invite/step-2-add.png';
import stepThreeImg from '../../assets/invite/step-3-allow.png';
import successImg from '../../assets/invite/success.png';

interface InviteCoachingModalProps {
  onClose: () => void;
  openerElement: HTMLButtonElement | null;
}

export function InviteCoachingModal({ onClose, openerElement }: InviteCoachingModalProps) {
  const [isDontShowChecked, setIsDontShowChecked] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  const handleClose = useCallback(() => {
    if (isDontShowChecked) {
      try {
        localStorage.setItem('emdr_invite_modal_suppressed', 'true');
      } catch (err) {
        console.warn('[InviteCoachingModal] Failed to persist suppression preference:', err);
      }
    }
    onClose();
  }, [onClose, isDontShowChecked]);

  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') handleClose();
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [handleClose]);

  useEffect(() => {
    const selector = 'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])';
    const focusable = Array.from(
      containerRef.current?.querySelectorAll<HTMLElement>(selector) ?? []
    );
    focusable[0]?.focus();

    return () => {
      // eslint-disable-next-line react-hooks/exhaustive-deps
      openerElement?.focus();
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key !== 'Tab') return;

    const selector = 'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])';
    const focusable = Array.from(
      containerRef.current?.querySelectorAll<HTMLElement>(selector) ?? []
    );
    const first = focusable[0];
    const last = focusable[focusable.length - 1];

    if (e.shiftKey) {
      if (document.activeElement === first) {
        e.preventDefault();
        last?.focus();
      }
    } else {
      if (document.activeElement === last) {
        e.preventDefault();
        first?.focus();
      }
    }
  };

  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) handleClose();
  };

  return (
    // Overlay covers the scrollable controls region only. The parent in TherapistPanel
    // is a `relative flex-1` wrapper that's a flex sibling of the preview strip and
    // action bar, so `inset-0` here automatically excludes both. No pixel math.
    <div
      className="absolute inset-0 z-50 flex items-start justify-center bg-black/50 p-3"
      onClick={handleBackdropClick}
    >
      {/* Card split into two regions:
          - Fixed header (flex-shrink-0): never scrolls. Holds the close button, the
            eyebrow chip, the title, and the subtitle so therapists always see what
            this panel is, even mid-scroll.
          - Scrollable body (flex-1, overflow-y-auto): step cards + checkbox + Got it.
          Splitting at the card level rather than using sticky-inside-padded-scroll
          guarantees nothing from the body can ever appear above the header. */}
      <div
        ref={containerRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="invite-modal-title"
        onKeyDown={handleKeyDown}
        className="relative bg-blue-50 rounded-xl shadow-xl ring-1 ring-sync-blue/10 w-full max-w-[340px] max-h-full flex flex-col overflow-hidden"
      >
        {/* Fixed header */}
        <header className="flex-shrink-0 px-4 pt-3 pb-3 border-b border-sync-blue/10">
          <div className="flex items-start justify-between gap-2 mb-2">
            {/* Eyebrow chip — at-a-glance category signal */}
            <span className="inline-flex items-center text-[10px] font-bold tracking-wider text-sync-blue uppercase bg-white px-2 py-0.5 rounded-full ring-1 ring-sync-blue/20">
              Patient onboarding
            </span>
            <button
              type="button"
              onClick={handleClose}
              aria-label="Close"
              className="-mt-1 -mr-1 h-8 w-8 inline-flex items-center justify-center rounded-full text-gray-500 hover:text-emdr-navy hover:bg-white transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sync-blue focus-visible:ring-offset-1"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                strokeWidth={2.25}
                stroke="currentColor"
                className="w-4 h-4"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
          </div>
          <h2 id="invite-modal-title" className="text-lg font-semibold text-emdr-navy leading-tight">
            Guide your patient
          </h2>
          <p className="text-xs text-text-muted mt-1">
            Invite sent. Read the steps aloud while they act.
          </p>
        </header>

        {/* Scrollable body */}
        <div className="flex-1 min-h-0 overflow-y-auto px-4 pt-4 pb-4">

        <section className="mb-5">
          <div className="flex items-start gap-2 mb-2">
            <span aria-hidden="true" className="inline-flex shrink-0 items-center justify-center w-5 h-5 rounded-full bg-sync-blue text-white text-[11px] font-semibold mt-0.5">1</span>
            <p className="text-[15px] leading-snug text-emdr-navy flex-1">
              Tell them:{' '}
              <span className="font-semibold">
                &ldquo;You&rsquo;ll see a small pop-up &mdash; click Open.&rdquo;
              </span>
            </p>
          </div>
          <img
            src={stepOneImg}
            width={520}
            height={329}
            alt="Zoom notification with the Open link circled"
            className="w-full h-auto rounded border border-gray-200"
          />
        </section>

        <div className="my-5 flex items-center gap-3 text-sm font-medium text-emdr-navy">
          <div className="flex-1 h-px bg-gray-200" />
          <span>First time using the app?</span>
          <div className="flex-1 h-px bg-gray-200" />
        </div>

        <section className="mb-5">
          <div className="flex items-start gap-2 mb-2">
            <span aria-hidden="true" className="inline-flex shrink-0 items-center justify-center w-5 h-5 rounded-full bg-sync-blue text-white text-[11px] font-semibold mt-0.5">2</span>
            <p className="text-[15px] leading-snug text-emdr-navy flex-1">
              Tell them:{' '}
              <span className="font-semibold">
                &ldquo;Click the blue Add button in the top right.&rdquo;
              </span>
            </p>
          </div>
          <img
            src={stepTwoImg}
            width={713}
            height={483}
            alt="Zoom App marketplace dialog with the blue Add button circled"
            className="w-full h-auto rounded border border-gray-200"
          />
        </section>

        <section className="mb-5">
          <div className="flex items-start gap-2 mb-2">
            <span aria-hidden="true" className="inline-flex shrink-0 items-center justify-center w-5 h-5 rounded-full bg-sync-blue text-white text-[11px] font-semibold mt-0.5">3</span>
            <p className="text-[15px] leading-snug text-emdr-navy flex-1">
              Tell them:{' '}
              <span className="font-semibold">
                &ldquo;You&rsquo;ll see a healthcare warning &mdash; that&rsquo;s normal. Check the box, then click Allow.&rdquo;
              </span>
            </p>
          </div>
          <img
            src={stepThreeImg}
            width={722}
            height={1432}
            alt="Healthcare disclaimer with checkbox and Allow button circled"
            className="w-full h-auto rounded border border-gray-200"
          />
        </section>

        <section className="mb-5 rounded-md bg-green-50 border border-green-200 p-3">
          <p className="text-[15px] leading-snug text-emdr-navy mb-2">
            <span className="font-semibold">Once they see this screen, they&rsquo;re in.</span>
          </p>
          <img
            src={successImg}
            width={714}
            height={1434}
            alt="Waiting for therapist screen"
            className="w-32 h-auto rounded border border-gray-200 mx-auto block"
          />
        </section>

        <label className="flex items-center gap-2 text-sm text-text-muted cursor-pointer mb-3">
          <input
            type="checkbox"
            checked={isDontShowChecked}
            onChange={(e) => setIsDontShowChecked(e.target.checked)}
            className="rounded border-gray-300 text-sync-blue focus:ring-sync-blue"
          />
          Don&apos;t show this again
        </label>

        <button
          type="button"
          onClick={handleClose}
          className="w-full min-h-[44px] py-3 rounded-lg font-semibold text-base transition-colors bg-sync-blue hover:bg-cta-hover text-white shadow-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sync-blue focus-visible:ring-offset-2"
        >
          Got it
        </button>
        </div>
      </div>
    </div>
  );
}
